<script setup lang="ts">
import { Check, Loader2, ThumbsDown, ThumbsUp } from 'lucide-vue-next';
import type { AcceptableValue } from 'reka-ui';
import { ref, watch } from 'vue';
import {
    rejectCell as rejectCellAction,
    update as updateCell,
    validateCell as validateCellAction,
} from '@/actions/App/Http/Controllers/ExtractionCellController';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/composables/useToast';

// ─── Props & Emits ─────────────────────────────────────────────────────────────

interface Props {
    rowId: string;
    columnId: string;
    gridId: string;
    type: string;
    config: Record<string, unknown> | null;
    initialValue: { text?: string } | null;
    cellId: string | null;
    status: string | null;
    aiGenerated: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    (e: 'update:value', value: { text?: string } | null): void;
    (e: 'update:status', status: string | null): void;
}>();

// ─── State ─────────────────────────────────────────────────────────────────────

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const toast = useToast();
const localText = ref(props.initialValue?.text ?? '');
const saveState = ref<SaveState>('idle');
const reviewLoading = ref(false);

watch(
    () => props.initialValue?.text,
    (incoming) => {
        if (incoming !== localText.value) {
            localText.value = incoming ?? '';
        }
    },
);

// ─── Status indicator ─────────────────────────────────────────────────────────

function statusBorderClass(): string {
    if (props.status === 'validated') {
        return 'border-l-4 border-l-green-500';
    }

    if (props.status === 'rejected') {
        return 'border-l-4 border-l-red-400';
    }

    if (props.status === 'extracted') {
        return 'border-l-4 border-l-violet-400';
    }

    return '';
}

// ─── CSRF helper ──────────────────────────────────────────────────────────────

function getCsrfToken(): string {
    return (
        (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)
            ?.content ?? ''
    );
}

// ─── Save logic (fetch, no Inertia) ──────────────────────────────────────────

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

async function save(text: string) {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
    }

    const value = text.trim() === '' ? null : { text };

    saveState.value = 'saving';

    try {
        const res = await fetch(updateCell.url(props.gridId), {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
                Accept: 'application/json',
            },
            body: JSON.stringify({
                row_id: props.rowId,
                column_id: props.columnId,
                value,
            }),
        });

        if (!res.ok) {
            throw new Error('save failed');
        }

        saveState.value = 'saved';
        emit('update:value', value);
        setTimeout(() => {
            if (saveState.value === 'saved') {
                saveState.value = 'idle';
            }
        }, 1500);
    } catch {
        saveState.value = 'error';
        toast.error("Impossible d'enregistrer la cellule.");
        localText.value = props.initialValue?.text ?? '';
        setTimeout(() => {
            if (saveState.value === 'error') {
                saveState.value = 'idle';
            }
        }, 2000);
    }
}

function scheduleDebounce(text: string) {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
    }

    debounceTimer = setTimeout(() => save(text), 500);
}

function onInput(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    localText.value = target.value;
    scheduleDebounce(target.value);
}

function onBlur() {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
        save(localText.value);
    }
}

function onBooleanChange(checked: boolean | 'indeterminate') {
    const value = checked === true ? 'true' : 'false';
    localText.value = value;
    save(value);
}

function onSelectChange(value: AcceptableValue) {
    if (typeof value !== 'string') {
        return;
    }

    localText.value = value;
    save(value);
}

function selectOptions(): string[] {
    const opts = props.config?.options;

    if (Array.isArray(opts)) {
        return opts as string[];
    }

    return [];
}

// ─── Validate / reject ───────────────────────────────────────────────────────

async function reviewCell(action: 'validate' | 'reject') {
    if (!props.cellId || reviewLoading.value) {
        return;
    }

    reviewLoading.value = true;

    const url =
        action === 'validate'
            ? validateCellAction.url(props.gridId)
            : rejectCellAction.url(props.gridId);

    try {
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': getCsrfToken(),
                Accept: 'application/json',
            },
            body: JSON.stringify({
                row_id: props.rowId,
                column_id: props.columnId,
            }),
        });

        if (!res.ok) {
            throw new Error('review failed');
        }

        emit('update:status', action === 'validate' ? 'validated' : 'rejected');
    } catch {
        toast.error('Impossible de mettre à jour le statut.');
    } finally {
        reviewLoading.value = false;
    }
}
</script>

<template>
    <div
        :class="[
            'group/cell relative min-h-[48px] w-full transition-colors',
            statusBorderClass(),
            saveState === 'error' && 'bg-destructive/5',
        ]"
    >
        <!-- Save indicator (top-right corner) -->
        <div
            class="pointer-events-none absolute top-2 right-2 z-10 flex h-4 w-4 items-center justify-center"
        >
            <Loader2
                v-if="saveState === 'saving'"
                class="h-3 w-3 animate-spin text-muted-foreground/50"
            />
            <Check
                v-else-if="saveState === 'saved'"
                class="h-3 w-3 text-emerald-500"
            />
        </div>

        <!-- Validate / reject buttons (visible on hover for ai_generated unreviewed cells) -->
        <div
            v-if="status === 'extracted' && cellId"
            class="absolute right-1 bottom-1 z-20 hidden items-center gap-0.5 group-hover/cell:flex"
        >
            <button
                type="button"
                :disabled="reviewLoading"
                class="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-green-100 hover:text-green-600 disabled:opacity-50"
                title="Valider"
                @click.stop="reviewCell('validate')"
            >
                <ThumbsUp class="h-3 w-3" />
            </button>
            <button
                type="button"
                :disabled="reviewLoading"
                class="flex h-5 w-5 items-center justify-center rounded text-muted-foreground/60 transition-colors hover:bg-red-100 hover:text-red-500 disabled:opacity-50"
                title="Refuser"
                @click.stop="reviewCell('reject')"
            >
                <ThumbsDown class="h-3 w-3" />
            </button>
        </div>

        <!-- text -->
        <Textarea
            v-if="type === 'text'"
            :model-value="localText"
            rows="2"
            placeholder="—"
            class="max-h-[96px] min-h-[48px] w-full resize-none rounded-none border-0 bg-transparent px-3 py-3 pr-7 text-sm shadow-none ring-0 transition-colors placeholder:text-muted-foreground/40 focus-visible:max-h-[240px] focus-visible:bg-accent/20 focus-visible:ring-0"
            @input="onInput"
            @blur="onBlur"
        />

        <!-- number -->
        <Input
            v-else-if="type === 'number'"
            type="number"
            :model-value="localText"
            placeholder="—"
            class="h-12 w-full rounded-none border-0 bg-transparent px-3 pr-7 shadow-none ring-0 transition-colors placeholder:text-muted-foreground/40 focus-visible:bg-accent/20 focus-visible:ring-0"
            @input="onInput"
            @blur="onBlur"
        />

        <!-- date -->
        <Input
            v-else-if="type === 'date'"
            type="date"
            :model-value="localText"
            class="h-12 w-full rounded-none border-0 bg-transparent px-3 pr-7 shadow-none ring-0 transition-colors focus-visible:bg-accent/20 focus-visible:ring-0"
            @input="onInput"
            @blur="onBlur"
        />

        <!-- boolean -->
        <div v-else-if="type === 'boolean'" class="flex h-12 items-center px-4">
            <Checkbox
                :model-value="localText === 'true'"
                @update:model-value="onBooleanChange"
            />
        </div>

        <!-- select -->
        <Select
            v-else-if="type === 'select'"
            :model-value="localText || undefined"
            @update:model-value="onSelectChange"
        >
            <SelectTrigger
                class="h-12 w-full rounded-none border-0 bg-transparent px-3 shadow-none ring-0 focus:ring-0 focus-visible:bg-accent/20"
            >
                <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
                <SelectItem
                    v-for="opt in selectOptions()"
                    :key="opt"
                    :value="opt"
                >
                    {{ opt }}
                </SelectItem>
            </SelectContent>
        </Select>

        <!-- multi_select — comma-separated text -->
        <Input
            v-else-if="type === 'multi_select'"
            :value="localText"
            placeholder="val1, val2, …"
            class="h-12 w-full rounded-none border-0 bg-transparent px-3 pr-7 shadow-none ring-0 transition-colors placeholder:text-muted-foreground/40 focus-visible:bg-accent/20 focus-visible:ring-0"
            @input="onInput"
            @blur="onBlur"
        />

        <!-- fallback for any unknown type -->
        <Input
            v-else
            :value="localText"
            placeholder="—"
            class="h-12 w-full rounded-none border-0 bg-transparent px-3 pr-7 shadow-none ring-0 transition-colors placeholder:text-muted-foreground/40 focus-visible:bg-accent/20 focus-visible:ring-0"
            @input="onInput"
            @blur="onBlur"
        />
    </div>
</template>
